/**
 * RoleRoute Component
 * 
 * Route guard that checks user role authorization.
 * Redirects unauthorized users to appropriate page based on their role.
 */

import { Navigate, Outlet } from 'react-router-dom';
import { useAuthStore, authSelectors } from '@/stores/authStore';
import type { UserRole } from '@/types/user';

/**
 * Props for RoleRoute component
 */
interface RoleRouteProps {
  /**
   * List of roles allowed to access this route
   * User must have one of these roles to proceed
   */
  allowedRoles: UserRole[];
}

/**
 * RoleRoute component
 * 
 * Wraps routes that require specific user roles.
 * If user doesn't have required role, redirects to appropriate page:
 * - External users → /external/payments
 * - Regular users → /home
 * - Admins trying to access external routes → /home
 * 
 * @example
 * ```tsx
 * <Route element={<RoleRoute allowedRoles={['Administrador']} />}>
 *   <Route path="/admin/users" element={<UsersPage />} />
 * </Route>
 * ```
 */
export function RoleRoute({ allowedRoles }: RoleRouteProps) {
  const userRole = useAuthStore(authSelectors.role);
  const user = useAuthStore(authSelectors.user);

  // Check if user has required role
  const hasRequiredRole = userRole && allowedRoles.includes(userRole);
  
  // Special case: users with tenantAdmin group can access Administrador routes
  const hasAdminGroup = user?.groups?.includes('tenantAdmin') ?? false;
  const requiresAdmin = allowedRoles.includes('Administrador');
  const hasAdminAccess = hasAdminGroup && requiresAdmin;

  if (!hasRequiredRole && !hasAdminAccess) {
    // Redirect based on user's actual role
    if (userRole === 'External') {
      // External users can only access external routes
      return <Navigate to="/external/payments" replace />;
    }
    
    // Regular users and admins go to home
    return <Navigate to="/home" replace />;
  }

  // Render child routes
  return <Outlet />;
}
